import { AppIcon } from '@/components/ui/app-icon';
import type { ReactNode } from 'react';
import { useUser } from '@clerk/react';
import { useLocation } from '@tanstack/react-router';
import { iconCircleCheck, iconDesktop } from '@/lib/icons';
import { BRAND } from '@/lib/brand';

/**
 * Full-screen overlay shown on phones and tablets (``touch-only``: under
 * Tailwind's lg, a finger as the primary pointer, no hover; see
 * ``TOUCH_ONLY_QUERY``). Only rendered inside <SignedIn>, so auth pages are
 * accessible on smaller screens.
 *
 * Width alone isn't the test: a laptop with a narrow window, browser zoom or
 * split screen is under 1024px too, and Replay showed those desktop users
 * locked out by this screen. Their primary pointer is a mouse or trackpad, so
 * they get through. `any-pointer: fine` let Android phones through as well.
 * The overlay is hidden unless the query matches, so a browser that can't
 * evaluate it shows the app. Wrapped application content uses the inverse
 * variant so blocked controls leave the visual and accessibility trees
 * together.
 *
 * Cleanlist staff get through on /management and /finance only: their pages
 * fold to a phone layout, and the shell hides its nav rail on touch-only
 * screens (every other page is behind this overlay there).
 */
export const MobileBlocker = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const { pathname } = useLocation();
  const firstName = user?.firstName;
  const isStaff = !!user?.primaryEmailAddress?.emailAddress
    ?.toLowerCase()
    .endsWith('@cleanlist.ai');
  if (
    isStaff &&
    (pathname.startsWith('/management') || pathname.startsWith('/finance'))
  )
    return <>{children}</>;

  return (
    <>
      <div
        role='dialog'
        aria-modal='true'
        aria-labelledby='mobile-blocker-title'
        aria-describedby='mobile-blocker-description'
        className='fixed inset-0 z-9999 hidden items-center justify-center bg-background p-6 touch-only:flex'
      >
        <div className='flex max-w-sm flex-col items-center text-center'>
          <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10'>
            <AppIcon
              icon={iconCircleCheck}
              className='text-green-500'
              style={{ fontSize: 24 }}
            />
          </div>
          <p className='mb-1 text-xl font-semibold tracking-tight text-foreground'>
            {firstName ? `Welcome, ${firstName}!` : 'Account Ready'}
          </p>
          <p className='mb-5 text-sm text-muted-foreground'>
            Your account is set up and ready to go.
          </p>

          <div className='mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-border bg-muted'>
            <AppIcon icon={iconDesktop} className='h-6 w-6 text-foreground' />
          </div>
          <h1
            id='mobile-blocker-title'
            className='mb-2 text-lg font-medium text-foreground'
          >
            Open {BRAND.name} on Desktop
          </h1>
          <p
            id='mobile-blocker-description'
            className='mb-5 text-sm leading-relaxed text-muted-foreground'
          >
            {BRAND.name} is a desktop-only platform built for managing leads,
            enrichment, and outreach. Open it on your computer to get started.
            Tablets and phones aren't supported yet.
          </p>
          <div className='rounded-lg border border-border bg-muted px-4 py-3'>
            <p className='text-xs text-muted-foreground'>
              Visit{' '}
              <span className='font-medium text-foreground'>
                {BRAND.appHost}
              </span>{' '}
              on your desktop to start using {BRAND.name}.
            </p>
          </div>
        </div>
      </div>
      <div className='contents touch-only:hidden'>{children}</div>
    </>
  );
};
